export interface RGB {
  r: number;
  g: number;
  b: number;
  a?: number; // Opacity (0-1), defaults to 1 if not provided
}

/**
 * Parses Hex, RGB, or RGBA strings into an {r, g, b, a?} object
 * Alpha channel (a) is returned as a number between 0 and 1
 */
export function parseToRgb(
  colorStr: string
): { r: number; g: number; b: number; a?: number } | null {
  if (typeof colorStr !== "string") return null;
  const str = colorStr.trim().toLowerCase();

  // 1. Strict Hex matching (No backtracking risk)
  const cleanHex = str.replace(/^#/, "");
  if (/^(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(cleanHex)) {
    let hex = cleanHex;
    let alpha: number | undefined;

    // Expand 3-digit or 4-digit hex by doubling each character
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    // Extract alpha from 8-digit hex (last 2 characters after expansion)
    if (hex.length === 8) {
      alpha = parseInt(hex.substring(6, 8), 16) / 255;
      hex = hex.substring(0, 6);
    }

    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
      ...(alpha !== undefined && { a: alpha }),
    };
  }

  // 2. Safe RGB/RGBA matching without overlapping whitespace quantifiers
  // Supports comma-separated "rgb(0,0,0)" and space-separated "rgb(0 0 0)"
  // Also captures optional alpha: "rgba(0,0,0,0.5)" or "rgba(0 0 0 / 0.5)"
  const rgbMatch = str.match(
    /^rgba?\(\s*([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:(?:\s*,\s*|\s*\/\s*)([\d.]+))?\s*\)/
  );

  if (rgbMatch) {
    const result: RGB = {
      r: Math.min(255, Math.max(0, Math.round(parseFloat(rgbMatch[1])))),
      g: Math.min(255, Math.max(0, Math.round(parseFloat(rgbMatch[2])))),
      b: Math.min(255, Math.max(0, Math.round(parseFloat(rgbMatch[3])))),
    };

    if (rgbMatch[4]) {
      result.a = Math.min(1, Math.max(0, parseFloat(rgbMatch[4])));
    }

    return result;
  }

  return null;
}

/**
 * Composites a color with opacity over a background color using alpha blending.
 * Handles semi-transparent colors by blending them with the background.
 */
function compositeColor(
  foreground: RGB,
  background: RGB = { r: 255, g: 255, b: 255 }
): RGB {
  const alpha = foreground.a ?? 1;
  const invAlpha = 1 - alpha;

  return {
    r: Math.round(foreground.r * alpha + background.r * invAlpha),
    g: Math.round(foreground.g * alpha + background.g * invAlpha),
    b: Math.round(foreground.b * alpha + background.b * invAlpha),
  };
}

/**
 * Returns '#000000' (Black) or '#FFFFFF' (White) based on input background color.
 * Considers opacity by compositing semi-transparent colors over white.
 */
export function getContrastTextColor(colorString: string) {
  const rgb = parseToRgb(colorString);

  // Fallback to black text if parsing fails
  if (!rgb) return "#000000";

  // Composite with white background if color has opacity
  const composited = compositeColor(rgb, { r: 255, g: 255, b: 255 });

  // YIQ Equation for human perceived brightness
  const brightness =
    (composited.r * 299 + composited.g * 587 + composited.b * 114) / 1000;

  return brightness >= 128 ? "#000000" : "#FFFFFF";
}

export const getLuminance = (colorStr: string): number => {
  const rgb = parseToRgb(colorStr) ?? { r: 0, g: 0, b: 0 };
  // Composite with white if color has opacity
  const composited = compositeColor(rgb, { r: 255, g: 255, b: 255 });
  const [r, g, b] = [composited.r, composited.g, composited.b].map((val) => {
    const normalized = val / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (c1: string, c2: string): number => {
  const lum1 = getLuminance(c1);
  const lum2 = getLuminance(c2);
  return (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
};

export function getAccessibleButtonStyles(
  pageBgColor: string,
  buttonBgColor: string,
  buttonTextColor: string,
  fallbackBgColor: string = "#000000",
  fallbackTextColor: string = "#FFFFFF"
) {
  if (!pageBgColor || !buttonBgColor) return null;
  // 1. Button BG vs Page BG
  const finalButtonBg =
    getContrastRatio(buttonBgColor, pageBgColor) >= 4.5
      ? buttonBgColor
      : fallbackBgColor;

  // 2. Button Text vs Final Button BG
  const finalButtonText =
    buttonTextColor && getContrastRatio(buttonTextColor, finalButtonBg) >= 4.5
      ? buttonTextColor
      : fallbackTextColor;

  return {
    buttonBgColor: finalButtonBg,
    buttonTextColor: finalButtonText,
  };
}
